import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, BookOpen, ChevronRight, Plus, ShieldCheck } from 'lucide-react';
import api from '../api';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';

function highlight(text, q) {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[#3a2f18] text-[#f5d67a] px-0.5 rounded">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

export default function Help() {
  const [data, setData] = useState({ categories: [], total: 0 });
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    let cancelled = false;
    api.get('/help/articles').then(r => { if (!cancelled) setData(r.data); }).catch(()=>{}).finally(()=>setLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Client-side filter — the list is small and the API returns everything.
  // Larger deployments should push search to the backend with a text index.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data.categories;
    return data.categories
      .map(c => ({ ...c, articles: c.articles.filter(a =>
        (a.title || '').toLowerCase().includes(needle) ||
        (a.excerpt || '').toLowerCase().includes(needle) ||
        (a.category || '').toLowerCase().includes(needle)
      )}))
      .filter(c => c.articles.length > 0);
  }, [data.categories, q]);

  const totalMatches = filtered.reduce((n, c) => n + c.articles.length, 0);

  return (
    <div className="bg-[#0a0a0b] text-zinc-100 min-h-screen">
      <SEO
        title="Reachly Help — walkthroughs & troubleshooting"
        description="Guides, walkthroughs and troubleshooting for Reachly — connect Outlook, build campaigns, import contacts, and more."
      />
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pt-16 pb-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm uppercase tracking-widest text-[#d4a94a]">Help center</div>
            <h1 className="mt-3 font-display text-5xl md:text-6xl">How can we help?</h1>
          </div>
          {user?.is_admin && (
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[10px] uppercase tracking-widest text-[#f5d67a] flex items-center gap-1"><ShieldCheck size={12}/>Admin</span>
              <Link to="/app/admin/help" data-testid="help-admin-open">
                <Button variant="outline" className="border-[#3a2f18] text-[#f5d67a] hover:bg-[#1a1815]">Manage articles</Button>
              </Link>
              <Link to="/app/admin/help" data-testid="help-admin-new">
                <Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium"><Plus size={14} className="mr-1"/>New article</Button>
              </Link>
            </div>
          )}
        </div>
        <p className="mt-5 text-zinc-400 max-w-2xl text-lg">
          Walkthroughs, setup guides and answers to the questions we hear most. Can't find something? Email <a href="mailto:hello@ireachly.com" className="text-[#f5d67a] hover:underline">hello@ireachly.com</a>.
        </p>
        <div className="mt-8 relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
          <Input
            value={q}
            onChange={e=>setQ(e.target.value)}
            placeholder="Search help articles…"
            className="pl-10 h-12 bg-[#111110] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]"
            data-testid="help-search-input"
          />
        </div>
        {q && (
          <div className="mt-3 text-xs text-zinc-500" data-testid="help-search-count">
            {totalMatches} result{totalMatches === 1 ? '' : 's'} for "{q}"
          </div>
        )}
      </section>

      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-24">
        {loading ? (
          <div className="text-zinc-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <Card className="bg-[#111110] border-zinc-900 p-10 text-center">
            <BookOpen className="mx-auto text-zinc-600 mb-3" size={28}/>
            <div className="font-display text-xl text-zinc-200">
              {data.total === 0 ? 'No articles yet.' : 'No articles matched.'}
            </div>
            <div className="text-sm text-zinc-500 mt-2">
              {data.total === 0 ? 'Check back soon — we\'re writing docs right now.' : 'Try a different search term.'}
            </div>
          </Card>
        ) : (
          <div className="space-y-8">
            {filtered.map(c => (
              <div key={c.slug || c.name} data-testid={`help-category-${c.slug || c.name}`}>
                <div className="text-xs uppercase tracking-widest text-[#d4a94a] mb-3">{c.name}</div>
                <div className="grid md:grid-cols-2 gap-3">
                  {c.articles.map(a => (
                    <Link key={a.id} to={`/help/${a.slug}`} data-testid={`help-article-link-${a.slug}`}>
                      <Card className="bg-[#111110] border-zinc-900 hover:border-[#3a2f18] transition-colors p-5 h-full">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="font-display text-lg text-zinc-100">{highlight(a.title, q)}</div>
                            {a.excerpt && <div className="text-sm text-zinc-400 mt-1 leading-relaxed">{highlight(a.excerpt, q)}</div>}
                          </div>
                          <ChevronRight size={18} className="text-[#d4a94a] shrink-0 mt-1"/>
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
